import { challenges } from "../contexts/ChallengeData";
import { ProfileContext } from "../contexts/AppContext";
import { useContext } from "react";

export const formatTime = (time: number) => {
  const sign = time < 0 ? "-" : "";
  const absTime = Math.abs(time);
  const hours = Math.trunc(absTime);
  const mins = String(Math.trunc((absTime - hours) * 60)).padStart(2, "0");
  return `${sign}${hours}:${mins}`;
};

export const happinessAngle = (happiness: number) => {
  return mapRange(happiness, getMinHappiness(), getMaxHappiness(), -75, 75);
};

const getMaxHappiness = () => {
  // for each challenge, get the highest happiness value from the options and add them together
  return challenges.reduce((acc, challenge) => {
    return acc + Math.max(...challenge.question.options.map((option) => option.happiness));
  }, 0);
};

const getMinHappiness = () => {
  // for each challenge, get the highest happiness value from the options and add them together
  return challenges.reduce((acc, challenge) => {
    return acc + Math.min(...challenge.question.options.map((option) => option.happiness));
  }, 0);
};

const mapRange = (value: number, inputMin: number, inputMax: number, outputMin: number, outputMax: number) => {
  return outputMin + ((outputMax - outputMin) / (inputMax - inputMin)) * (value - inputMin);
};

const getBestTime = () => {
  // for each challenge, get the lowest time value from the options and add them together
  return challenges.reduce((acc, challenge) => {
    return acc + Math.min(...challenge.question.options.map((option) => option.time));
  }, 0);
};

const getWorstTime = () => {
  // for each challenge, get the highest time value from the options and add them together
  return challenges.reduce((acc, challenge) => {
    return acc + Math.max(...challenge.question.options.map((option) => option.time));
  }, 0);
};

export const calculateScore = () => {
  const { happiness, time, battery, timeBonus } = useContext(ProfileContext);
  if (happiness === undefined || time === undefined || battery === undefined || timeBonus === undefined) return 0;

  const bestTime = getBestTime();
  const worstTime = getWorstTime();
  const bestHappiness = getMaxHappiness();
  const worstHappiness = getMinHappiness();

  // Calculate the time score
  const timeScore = mapRange(time, worstTime, bestTime, 1, 100);

  // Calculate the happiness score
  const happinessScore = mapRange(happiness, worstHappiness, bestHappiness, 1, 100);

  // battery is already from 0 to 100
  const batteryScore = mapRange(battery, 0, 100, 1, 100);

  // Scale the scores to achieve a range closer to 0-9999
  const reductionFactor = 100;
  const totalScore = (timeScore * happinessScore * batteryScore) / reductionFactor + timeBonus / reductionFactor;
  return Math.round(totalScore);
};

// Add the score to Neflify Blobs
// This function will be called when the user submits their score
export async function persistScore(score: {
  firstName: string;
  lastName: string;
  carColor: string;
  concertType: string;
  score: number;
}) {


  const body = JSON.stringify(score);
  const response = await fetch("/.netlify/functions/set-score", {
    method: "POST",
    body: body,
  });

  if (!response.ok) {
    console.error("Failed to submit score");
    return { success: false, error: "Failed to submit score" };
  }

  const result = await response.json();
  console.log("Submitted! Score ID:", result.score.key);

  return { success: true, data: result };
}

// Retrieve the score from Netlify Blobs
// This function will be called when the user hits the leaderboard page
export async function getRemoteScore(key: string) {
  const response = await fetch(`/.netlify/functions/get-score?key=${key}`, {
    method: "GET",
  });

  if (!response.ok) {
    console.error("Failed to fetch score");
    return { success: false, error: "Failed to fetch score" };
  }

  const result = await response.json();
  if(result.score) {
    console.log("Fetched! Score ID:", result.score.key);
  }
  else {
    console.log("Fetched top scores: ", result.topScores.length);
  }
  
  return { success: true, data: result };
}
